import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useConfirm } from "@/context/ConfirmContext";
import { Plus, Pencil, Trash2, Building2, Users } from "lucide-react";

const empty = {
  name: "", brief: "", website: "", logo_url: "", logo_file_id: "",
  representative_name: "", representative_role: "", representative_email: "",
  representative_linkedin: "", representative_phone: "", representative_bio: "",
  representative2_name: "", representative2_role: "", representative2_email: "",
  representative2_linkedin: "", representative2_phone: "", representative2_bio: "",
  emergency_contact: "",
  internal_notes: "",
  hide_phone_from_teams: true, hide_emergency_from_teams: true,
  team_ids: [],
};

function ContactFields({ form, setForm, prefix, title, testPrefix }) {
  const f = (k) => `${prefix}${k}`;
  const set = (k) => (e) => setForm({ ...form, [f(k)]: e.target.value });
  return (
    <div className="border-t border-[#E5E7EB] pt-4" data-testid={`${testPrefix}-section`}>
      <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mb-2">{title}</div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label>Name <span className="text-[#25AAAA] normal-case font-normal">· students see</span></Label>
          <Input value={form[f("name")] || ""} onChange={set("name")} data-testid={`${testPrefix}-name`} />
        </div>
        <div>
          <Label>Position <span className="text-[#25AAAA] normal-case font-normal">· students see</span></Label>
          <Input value={form[f("role")] || ""} onChange={set("role")} data-testid={`${testPrefix}-role`} />
        </div>
        <div className="md:col-span-2">
          <Label>Short biography <span className="text-[#25AAAA] normal-case font-normal">· students see</span></Label>
          <Textarea rows={3} value={form[f("bio")] || ""} onChange={set("bio")} placeholder="Two or three lines — who they are and what they do at the business."
            data-testid={`${testPrefix}-bio`} />
        </div>
        <div>
          <Label>Email <span className="text-[#525252] normal-case font-normal">· admin only</span></Label>
          <Input value={form[f("email")] || ""} onChange={set("email")} data-testid={`${testPrefix}-email`} />
        </div>
        <div>
          <Label>Phone <span className="text-[#525252] normal-case font-normal">· admin only</span></Label>
          <Input value={form[f("phone")] || ""} onChange={set("phone")} data-testid={`${testPrefix}-phone`} />
        </div>
        <div className="md:col-span-2">
          <Label>LinkedIn <span className="text-[#525252] normal-case font-normal">· admin only</span></Label>
          <Input value={form[f("linkedin")] || ""} onChange={set("linkedin")} data-testid={`${testPrefix}-linkedin`} />
        </div>
      </div>
    </div>
  );
}

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const logoSrc = (form) => {
  if (form?.logo_file_id) {
    const tok = encodeURIComponent(localStorage.getItem("weave_token") || "");
    return `${BACKEND}/api/files/${form.logo_file_id}/download?auth=${tok}`;
  }
  return form?.logo_url || "";
};

export default function Sponsors() {
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showSecond, setShowSecond] = useState(false);

  const load = async () => {
    const [s, t] = await Promise.all([
      api.get("/sponsors").then((r) => r.data).catch(() => []),
      api.get("/teams").then((r) => r.data).catch(() => []),
    ]);
    setItems(s || []);
    setTeams(t || []);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setShowSecond(false); setOpen(true); };
  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...empty, ...s, team_ids: s.team_ids || [] });
    setShowSecond(!!(s.representative2_name || s.representative2_email));
    setOpen(true);
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      if (editing) {
        await api.put(`/sponsors/${editing.id}`, form);
        toast.success("Sponsor updated");
      } else {
        await api.post("/sponsors", form);
        toast.success("Sponsor created");
      }
      setOpen(false);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (s) => {
    if (!(await confirm({ title: `Delete ${s.name}?`, description: "This removes the sponsor from the platform.", destructive: true }))) return;
    await api.delete(`/sponsors/${s.id}`);
    toast.success("Deleted");
    await load();
  };

  const toggleTeam = (tid) => {
    setForm((f) => {
      const has = (f.team_ids || []).includes(tid);
      return { ...f, team_ids: has ? f.team_ids.filter((x) => x !== tid) : [...(f.team_ids || []), tid] };
    });
  };

  return (
    <div className="space-y-6" data-testid="sponsors-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-display text-3xl font-black tracking-tight text-[#0F1F2E]">Sponsors</h2>
          <p className="text-sm text-[#525252] mt-1">Real businesses running the team challenges. Assign one or more teams per sponsor.</p>
        </div>
        <Button onClick={openNew} className="bg-[#25AAAA] hover:bg-[#1e7d7d] rounded-none" data-testid="sponsor-new-btn">
          <Plus size={16} className="mr-2" /> Add sponsor
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E7EB] bg-white p-10 text-center" data-testid="sponsors-empty">
          <Building2 size={32} className="mx-auto text-[#525252]" />
          <p className="text-sm text-[#525252] mt-3">No sponsors yet. Add your first business to get teams matched up.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((s) => {
            const assignedTeams = (s.team_ids || []).map((tid) => teams.find((t) => t.id === tid)).filter(Boolean);
            const logo = logoSrc(s);
            return (
              <div key={s.id} className="border border-[#E5E7EB] bg-white p-5" data-testid={`sponsor-card-${s.id}`}>
                <div className="flex items-start gap-4">
                  {logo ? (
                    <img src={logo} alt={s.name} className="w-14 h-14 object-contain border border-[#E5E7EB] p-1"/>
                  ) : (
                    <div className="w-14 h-14 bg-[#0F1F2E] text-white flex items-center justify-center">
                      <Building2 size={22} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-xl font-bold tracking-tight text-[#0F1F2E] truncate">{s.name}</div>
                    {s.website && <div className="text-xs text-[#525252] truncate">{s.website}</div>}
                    {s.brief && <p className="text-sm text-[#525252] mt-2 line-clamp-3">{s.brief}</p>}
                    {(s.representative_name || s.representative2_name) && (
                      <div className="text-xs text-[#525252] mt-2" data-testid={`sponsor-card-contacts-${s.id}`}>
                        <span className="font-semibold text-[#0F1F2E]">Contacts:</span> {[s.representative_name, s.representative2_name].filter(Boolean).join(" · ")}
                      </div>
                    )}
                    <div className="mt-3 flex items-center gap-1.5 flex-wrap">
                      <Users size={12} className="text-[#25AAAA]" />
                      {assignedTeams.length === 0
                        ? <span className="text-[11px] uppercase tracking-wider text-[#f4b847] font-semibold">No team yet</span>
                        : assignedTeams.map((t) => (
                          <span key={t.id} className="text-[11px] bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5">{t.name}</span>
                        ))
                      }
                    </div>
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" className="rounded-none" onClick={() => openEdit(s)} data-testid={`sponsor-edit-${s.id}`}>
                    <Pencil size={12} className="mr-1.5" /> Edit
                  </Button>
                  <Button size="sm" variant="outline" className="rounded-none border-[#E93E36] text-[#E93E36] hover:bg-[#FDEDED]" onClick={() => remove(s)} data-testid={`sponsor-delete-${s.id}`}>
                    <Trash2 size={12} className="mr-1.5" /> Delete
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sponsor-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit sponsor" : "Add sponsor"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Business name *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sponsor-input-name"/>
            </div>
            <div>
              <Label>Challenge brief (students see this)</Label>
              <Textarea rows={4} value={form.brief} onChange={(e) => setForm({ ...form, brief: e.target.value })} data-testid="sponsor-input-brief"/>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <Label>Website</Label>
                <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}/>
              </div>
              <div>
                <Label>Logo</Label>
                <div className="mt-1 flex items-center gap-3">
                  {logoSrc(form) ? (
                    <img src={logoSrc(form)} alt="" className="w-14 h-14 object-contain border border-[#E5E7EB] p-1 bg-white"/>
                  ) : (
                    <div className="w-14 h-14 bg-[#F3F4F6] border border-dashed border-[#E5E7EB] flex items-center justify-center text-[10px] uppercase text-[#525252]">None</div>
                  )}
                  <label className="cursor-pointer border border-[#0F1F2E] px-3 py-2 text-[11px] font-semibold uppercase tracking-wider hover:bg-[#0F1F2E] hover:text-white transition-colors">
                    <input type="file" accept="image/*" className="hidden"
                      onChange={async (e) => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        try {
                          const fd = new FormData();
                          fd.append("file", f);
                          const { data } = await api.post("/files/upload?tag=sponsor-logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
                          setForm({ ...form, logo_file_id: data.id, logo_url: "" });
                          toast.success("Logo uploaded");
                        } catch (err) {
                          toast.error(err.response?.data?.detail || "Upload failed");
                        }
                        e.target.value = "";
                      }}
                      data-testid="sponsor-logo-upload"/>
                    Choose image
                  </label>
                  {(form.logo_file_id || form.logo_url) && (
                    <button type="button" className="text-[11px] uppercase tracking-wider text-[#E93E36] hover:underline"
                      onClick={() => setForm({ ...form, logo_file_id: "", logo_url: "" })}
                      data-testid="sponsor-logo-clear">Remove</button>
                  )}
                </div>
              </div>
            </div>

            <ContactFields form={form} setForm={setForm} prefix="representative_" title="Point of contact 1" testPrefix="sponsor-contact1" />

            {showSecond || form.representative2_name || form.representative2_email ? (
              <>
                <ContactFields form={form} setForm={setForm} prefix="representative2_" title="Point of contact 2" testPrefix="sponsor-contact2" />
                <button type="button" className="text-[11px] uppercase tracking-wider text-[#E93E36] hover:underline"
                  onClick={() => { setShowSecond(false); setForm({ ...form, representative2_name: "", representative2_role: "", representative2_email: "", representative2_linkedin: "", representative2_phone: "", representative2_bio: "" }); }}
                  data-testid="sponsor-contact2-remove">Remove second contact</button>
              </>
            ) : (
              <button type="button" onClick={() => setShowSecond(true)}
                className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-[#25AAAA] hover:underline"
                data-testid="sponsor-contact2-add">
                <Plus size={12} /> Add a second point of contact
              </button>
            )}

            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label>Emergency contact (admin only)</Label>
                  <Input value={form.emergency_contact} onChange={(e) => setForm({ ...form, emergency_contact: e.target.value })}/>
                </div>
              </div>
              <p className="text-[11px] text-[#525252] mt-2">Students only ever see each contact's name, position and biography. Emails, phone numbers and LinkedIn stay admin-only.</p>
            </div>

            <div className="border-t border-[#E5E7EB] pt-4">
              <div className="text-[10px] font-semibold tracking-[0.16em] uppercase text-[#525252] mb-2">Assign teams</div>
              <div className="flex flex-wrap gap-2">
                {teams.map((t) => {
                  const on = (form.team_ids || []).includes(t.id);
                  return (
                    <button key={t.id} type="button" onClick={() => toggleTeam(t.id)}
                      data-testid={`sponsor-team-toggle-${t.id}`}
                      className={`px-3 py-1.5 text-xs font-semibold border transition-colors ${
                        on ? "bg-[#25AAAA] text-white border-[#25AAAA]" : "bg-white text-[#525252] border-[#E5E7EB] hover:border-[#25AAAA]"
                      }`}>{t.name}</button>
                  );
                })}
              </div>
              <p className="text-[11px] text-[#525252] mt-2">3-team mode: 1 team per sponsor. 6-team mode: 2 teams per sponsor (they compete).</p>
            </div>

            <div className="border-t border-[#E5E7EB] pt-4">
              <Label>Internal notes (admin only)</Label>
              <Textarea rows={3} value={form.internal_notes} onChange={(e) => setForm({ ...form, internal_notes: e.target.value })}/>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Cancel</Button>
            <Button disabled={busy} className="bg-[#25AAAA] hover:bg-[#1e7d7d] rounded-none" onClick={save} data-testid="sponsor-save-btn">
              {busy ? "Saving…" : (editing ? "Save changes" : "Create sponsor")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
